import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { DocumentContent } from '../types';
import { FileAudio, FileText, X } from 'lucide-react';

interface ThumbnailListProps {
  attachments: DocumentContent[];
  onRemove: (index: number) => void;
  scrollToBottom?: () => void; // Direct scroll function
}

// Utility functions
const isAudioFile = (format: string): boolean => {
  const normalizedFormat = format.toLowerCase();
  
  // Check for common audio file extensions
  const audioExtensions = ['wav', 'mp3', 'mpeg', 'm4a', 'aac', 'ogg', 'flac', 'webm', 'opus'];
  if (audioExtensions.includes(normalizedFormat)) {
    return true;
  }
  
  // Check for MIME types (fallback)
  const audioMimeTypes = [
    'audio/wav', 'audio/mp3', 'audio/mpeg', 'audio/mp4', 'audio/m4a', 
    'audio/aac', 'audio/ogg', 'audio/flac', 'audio/webm', 'audio/opus',
    'audio/x-wav', 'audio/x-mp3', 'audio/x-mpeg'
  ];
  if (audioMimeTypes.includes(normalizedFormat)) {
    return true;
  }
  
  // Check if it starts with 'audio/'
  return normalizedFormat.startsWith('audio/');
};

const isTextFile = (format: string): boolean => {
  const textFormats = ['txt', 'text', 'text/plain'];
  return textFormats.includes(format.toLowerCase());
};

const isPdfFile = (format: string): boolean => {
  const normalizedFormat = format.toLowerCase();
  return normalizedFormat === 'pdf' || normalizedFormat === 'application/pdf';
};

const getDisplayFormat = (format: string): string => {
  const normalizedFormat = format.toLowerCase();
  
  // Map file extensions and MIME types to display formats
  const formatMap: Record<string, string> = {
    // File extensions
    'mpeg': 'MP3',
    'mp3': 'MP3',
    'm4a': 'M4A',
    'aac': 'AAC',
    'ogg': 'OGG',
    'flac': 'FLAC',
    'webm': 'WEBM',
    'opus': 'OPUS',
    'wav': 'WAV',
    // MIME types
    'audio/mpeg': 'MP3',
    'audio/mp3': 'MP3',
    'audio/mp4': 'M4A',
    'audio/m4a': 'M4A',
    'audio/aac': 'AAC',
    'audio/ogg': 'OGG',
    'audio/flac': 'FLAC',
    'audio/webm': 'WEBM',
    'audio/opus': 'OPUS',
    'audio/wav': 'WAV',
    'audio/x-wav': 'WAV',
    'audio/x-mp3': 'MP3',
    'audio/x-mpeg': 'MP3'
  };
  
  // Check if it's in our format map
  if (formatMap[normalizedFormat]) {
    return formatMap[normalizedFormat];
  }
  
  // Otherwise, just uppercase the format
  return format.toUpperCase();
};

export const ThumbnailList: React.FC<ThumbnailListProps> = ({
  attachments,
  onRemove,
  scrollToBottom,
}) => {
  const [selectedFile, setSelectedFile] = useState<DocumentContent | null>(null);

  // Scroll to bottom when new files are added
  useEffect(() => {
    if (attachments.length > 0 && scrollToBottom) {
      scrollToBottom();
    }
  }, [attachments.length, scrollToBottom]);

  const handleFileClick = (attachment: DocumentContent) => {
    // Only show preview for images (not audio, text, or PDF files)
    if (!isAudioFile(attachment.format) && !isTextFile(attachment.format) && !isPdfFile(attachment.format)) {
      setSelectedFile(attachment);
    }
  };

  const handleRemoveClick = (index: number, fileName: string) => {
    if (window.confirm(`Are you sure you want to remove "${fileName}"?`)) {
      onRemove(index);
    }
  };

  const handleCloseModal = () => {
    setSelectedFile(null);
  };

  const handleModalClick = (e: React.MouseEvent) => {
    // Close modal when clicking on the backdrop
    if (e.target === e.currentTarget) {
      setSelectedFile(null);
    }
  };

  if (attachments.length === 0) {
    return null;
  }

  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
        {attachments.map((attachment, index) => {
          const isAudio = isAudioFile(attachment.format);
          const isText = isTextFile(attachment.format);
          const isPdf = isPdfFile(attachment.format);
          
          return (
            <div key={index} className="relative group">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200 p-2 text-center">
                {isAudio ? (
                  <div className="space-y-1">
                    <div className="w-12 h-12 mx-auto bg-blue-50 rounded-lg flex items-center justify-center">
                      <FileAudio className="w-6 h-6 text-blue-600" />
                    </div>
                    <div className="text-xs text-gray-500">{getDisplayFormat(attachment.format)}</div>
                  </div>
                ) : isText ? (
                  <div className="space-y-1">
                    <div className="w-12 h-12 mx-auto bg-gray-50 rounded-lg flex items-center justify-center">
                      <FileText className="w-6 h-6 text-gray-600" />
                    </div>
                    <div className="text-xs text-gray-500">TXT</div>
                  </div>
                ) : isPdf ? (
                  <div className="space-y-1">
                    <div className="w-12 h-12 mx-auto bg-red-50 rounded-lg flex items-center justify-center">
                      <FileText className="w-6 h-6 text-red-600" />
                    </div>
                    <div className="text-xs text-gray-500">PDF</div>
                  </div>
                ) : (
                  <div 
                    className="cursor-pointer"
                    onClick={() => handleFileClick(attachment)}
                  >
                    <div className="w-12 h-12 mx-auto bg-gray-50 rounded-lg overflow-hidden">
                      <img
                        src={`data:image/${attachment.format};base64,${attachment.content}`}
                        alt={attachment.name}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-200"
                      />
                    </div>
                  </div>
                )}
                
                {/* Remove button - always visible */}
                <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  <button
                    type="button"
                    className="w-5 h-5 bg-gray-800 text-white rounded-full flex items-center justify-center hover:bg-black transition-colors duration-200 shadow-md"
                    onClick={() => handleRemoveClick(index, attachment.name)}
                    aria-label={`Remove ${attachment.name}`}
                  >
                    <X className="w-2.5 h-2.5" />
                  </button>
                </div>
              </div>
              
              <p className="text-xs text-gray-600 mt-1 truncate text-center px-1" title={attachment.name}>
                {attachment.name}
              </p>
            </div>
          );
        })}
      </div>

      {/* Image Preview Modal - Full Screen */}
      {selectedFile && createPortal(
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}
          onClick={handleModalClick}
        >
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Image Preview</h3>
              <button
                type="button"
                className="w-8 h-8 bg-gray-100 text-gray-600 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors duration-200"
                onClick={handleCloseModal}
                aria-label="Close preview"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            
            <div className="p-6">
              <div className="text-center">
                <img
                  src={`data:image/${selectedFile.format};base64,${selectedFile.content}`}
                  alt={selectedFile.name}
                  className="max-w-full max-h-96 object-contain rounded-lg mx-auto"
                />
                <h4 className="text-lg font-medium text-gray-900 mt-4">{selectedFile.name}</h4>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}; 